// Small helpers so the catalog files read like a list: one call per exercise, group defaults set once per file.
// All steps in the catalog are our own short wording (project rule: no copied instruction text).
import type { CameraView, Counting, CountingStatus, Exercise } from '../types';

export const camera = (status: CountingStatus = 'experimental'): Counting => ({ mode: 'camera', status });
export const TIMED: Counting = { mode: 'time' };
export const MANUAL: Counting = { mode: 'manual' };

const at = (view: CameraView, distanceM?: number) => (distanceM ? { view, distanceM } : { view });
export const FRONT = at('front', 2);
export const DIAGONAL = at('diagonal', 2);
export const SIDE = at('side', 2.5);
export const ANY = at('any');

/** Everything about an exercise except id, name, muscles and steps; the group defaults fill the rest. */
export type ExerciseFields = Partial<Omit<Exercise, 'id' | 'name' | 'muscles' | 'steps'>>;

type Defaults = ExerciseFields & Pick<Exercise, 'equipment' | 'counting' | 'camera' | 'intensity'>;

/**
 * Returns a maker for one group of exercises (for example all dumbbell lifts).
 * Fields given per exercise override the group defaults.
 */
export function group(defaults: Defaults) {
  return (id: string, name: string, muscles: string[], steps: string[], fields: ExerciseFields = {}): Exercise => ({
    demo: null,
    ...defaults,
    ...fields,
    id,
    name,
    muscles,
    steps,
  });
}

/**
 * The muscle words the catalog uses, so filters and the muscle map later see one vocabulary.
 * Lowercase, plain English.
 */
export const MUSCLES = [
  'chest', 'upper chest', 'shoulders', 'front delts', 'side delts', 'rear delts', 'triceps', 'biceps', 'forearms', 'grip',
  'back', 'upper back', 'lats', 'traps', 'lower back', 'neck',
  'core', 'abs', 'obliques', 'hip flexors',
  'glutes', 'hips', 'adductors', 'abductors', 'quads', 'hamstrings', 'calves', 'shins', 'ankles',
  'spine', 'wrists', 'balance', 'full body', 'heart and lungs',
] as const;
